import { useScrollReveal } from "@/hooks/useScrollReveal";
import { useTranslation } from "react-i18next";

const directions = ["reveal-left", "reveal-up", "reveal-right"] as const;

export default function Features() {
  const { t } = useTranslation();
  const heading = useScrollReveal<HTMLDivElement>();
  const cards = useScrollReveal<HTMLDivElement>({ threshold: 0.1 });

  const items = t("features.items", { returnObjects: true }) as {
    title: string;
    desc: string;
  }[];

  return (
    <section className="w-full px-6 py-24 md:py-32" aria-labelledby="principles-title">
      <div className="max-w-[1400px] mx-auto">
        {/* Section header - editorial display + serif lede */}
        <div
          ref={heading.ref}
          className={`reveal reveal-blur ${heading.isVisible ? "visible" : ""}`}
        >
          <div className="flex items-center gap-4 ed-eyebrow text-muted-foreground">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-brand" />
            <span>{t("features.eyebrow")}</span>
          </div>

          <div className="mt-8 grid gap-8 md:grid-cols-12 md:items-end">
            <h2
              id="principles-title"
              className="ed-display md:col-span-7"
            >
              {t("features.line1")}<br />{t("features.line2")}<span className="text-brand">.</span>
            </h2>
            <p className="ed-serif text-2xl md:text-3xl text-foreground/85 md:col-span-5">
              {t("features.sub")}
            </p>
          </div>
        </div>

        <div className="mt-16 ed-rule" />

        {/* Numbered editorial rows */}
        <div ref={cards.ref}>
          {items.map((item, i) => (
            <div
              key={item.title}
              className={`group grid gap-6 md:grid-cols-12 md:gap-10 items-baseline py-10 md:py-14 border-b border-border reveal ${directions[i]} reveal-delay-${i + 1} ${cards.isVisible ? "visible" : ""}`}
            >
              <div className="ed-eyebrow text-muted-foreground md:col-span-2 flex items-center gap-3">
                <span className="text-foreground/70 transition-colors duration-300 group-hover:text-brand">
                  0{i + 1}
                </span>
                <span className="inline-block h-px w-8 bg-border md:hidden" />
              </div>

              <h3 className="ed-headline md:col-span-4">
                {item.title}
              </h3>

              <p className="text-muted-foreground leading-relaxed text-lg md:col-span-6 md:max-w-xl">
                {item.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
